import React from 'react';
import styled from 'styled-components';

const ModeGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 8px;
  border: 1px solid var(--dcm-cms-color-mode-border-color, var(--puck-color-border, #d0d4d8));
  border-radius: var(--dcm-cms-color-mode-radius, var(--puck-radius-m, 6px));
  overflow: hidden;
`;

const ModeOption = styled.label.withConfig({
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>`
  position: relative;
  padding: 10px 8px;
  text-align: center;
  font-size: var(--dcm-cms-color-mode-font-size, var(--puck-field-font-size, 14px));
  font-weight: 600;
  color: ${({ $selected }) =>
    $selected
      ? 'var(--dcm-cms-color-mode-selected-text-color, #1F4F40)'
      : 'var(--dcm-cms-color-mode-text-color, var(--puck-color-text, #121516))'};
  background: ${({ $selected }) =>
    $selected
      ? 'var(--dcm-cms-color-mode-selected-bg, rgba(31, 79, 64, 0.08))'
      : 'var(--dcm-cms-color-mode-bg, var(--puck-field-color-bg, #fff))'};
  cursor: pointer;

  & + & {
    border-left: 1px solid var(--dcm-cms-color-mode-border-color, var(--puck-color-border, #d0d4d8));
  }

  &:focus-within {
    outline: 2px solid var(--dcm-cms-color-mode-focus-ring, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: -2px;
  }

  &:has(input:disabled) {
    cursor: default;
    color: var(--dcm-cms-color-mode-disabled-text-color, var(--puck-color-text-secondary, #646d72));
  }
`;

const ModeInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
`;

type ColorModeControlProps = {
  label: string;
  name: string;
  emptyLabel: string;
  isEmpty: boolean;
  onChange: (empty: boolean) => void;
  readOnly?: boolean;
};

/** Two semantic modes over an existing saved color sentinel, without adding a mode prop to page data. */
export function ColorModeControl({
  label,
  name,
  emptyLabel,
  isEmpty,
  onChange,
  readOnly,
}: ColorModeControlProps): JSX.Element {
  return (
    <ModeGroup role='radiogroup' aria-label={label}>
      <ModeOption $selected={isEmpty}>
        <ModeInput
          type='radio'
          name={`${name}-color-mode`}
          checked={isEmpty}
          onChange={() => onChange(true)}
          disabled={readOnly}
        />
        {emptyLabel}
      </ModeOption>
      <ModeOption $selected={!isEmpty}>
        <ModeInput
          type='radio'
          name={`${name}-color-mode`}
          checked={!isEmpty}
          onChange={() => onChange(false)}
          disabled={readOnly}
        />
        Custom
      </ModeOption>
    </ModeGroup>
  );
}
